import React from "react";
import { Zap, Brain, GitBranch, CheckCircle2 } from "lucide-react";

/**
 * Compact flow builder shown in hero — a miniature companion to the chat demo
 * that shows "this is the flow powering that conversation".
 */
export default function MiniFlow() {
  return (
    <div
      data-testid="hero-mini-flow"
      className="relative rounded-[20px] border border-white/10 bg-[#0b0b13] overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono tracking-[0.14em] uppercase text-white/50">
            flow · refunds_v4
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#3DDFB3]/12 border border-[#3DDFB3]/30 text-[#3DDFB3] font-mono">
            <span className="w-1 h-1 rounded-full bg-[#3DDFB3] soft-pulse" />
            running
          </span>
        </div>
        <span className="text-[10px] font-mono text-white/40">
          3 nodes · 12ms
        </span>
      </div>

      {/* Canvas */}
      <div className="relative h-[180px] bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.05)_1px,transparent_0)] [background-size:14px_14px]">
        {/* Edges */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path
            d="M 11 60 C 18 60, 22 50, 30 50"
            stroke="#7C5CFF"
            strokeOpacity="0.65"
            fill="none"
            className="flow-dash"
            vectorEffect="non-scaling-stroke"
            style={{ strokeWidth: 1.2 }}
          />
          <path
            d="M 48 50 L 62 50"
            stroke="#7C5CFF"
            strokeOpacity="0.65"
            fill="none"
            className="flow-dash"
            vectorEffect="non-scaling-stroke"
            style={{ strokeWidth: 1.2 }}
          />
          <path
            d="M 80 50 L 94 50"
            stroke="#3DDFB3"
            strokeOpacity="0.75"
            fill="none"
            className="flow-dash"
            vectorEffect="non-scaling-stroke"
            style={{ strokeWidth: 1.2 }}
          />
        </svg>

        {/* Nodes */}
        <MiniNode
          x="2%"
          y="46%"
          color="#7C5CFF"
          icon={Zap}
          title="Trigger"
          sub="webhook"
        />
        <MiniNode
          x="30%"
          y="38%"
          color="#7C5CFF"
          icon={Brain}
          title="Classifier"
          sub="gpt-4o-mini"
          meta="refund · 0.94"
        />
        <MiniNode
          x="62%"
          y="38%"
          color="#F97316"
          icon={GitBranch}
          title="Route"
          sub="if refund"
        />
        <MiniNode
          x="86%"
          y="40%"
          color="#3DDFB3"
          icon={CheckCircle2}
          title="Resolve"
          sub="auto"
          compact
        />
      </div>

      {/* Footer: run trace */}
      <div className="px-4 py-2.5 border-t border-white/5 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-white/40">
          run · 0x9a2
        </span>
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/10 text-white/60">
          kb · refunds_policy_v3
        </span>
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#3DDFB3]/10 border border-[#3DDFB3]/20 text-[#3DDFB3]">
          tool · shopify.refund
        </span>
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#7C5CFF]/10 border border-[#7C5CFF]/20 text-[#b9a9ff] ml-auto">
          ₹2,499
        </span>
      </div>
    </div>
  );
}

function MiniNode({ x, y, color, icon: Icon, title, sub, meta, compact }) {
  return (
    <div
      className="absolute rounded-md border backdrop-blur-sm"
      style={{
        left: x,
        top: y,
        background: `${color}15`,
        borderColor: `${color}55`,
        width: compact ? 68 : 96,
        boxShadow: `0 0 18px ${color}20`,
      }}
    >
      <div className="px-2 py-1.5">
        <div className="flex items-center gap-1 text-[10px] text-white/90">
          <Icon className="w-3 h-3" style={{ color }} />
          <span className="font-medium truncate">{title}</span>
        </div>
        <div className="text-[9px] text-white/45 truncate">{sub}</div>
        {meta && (
          <div className="mt-1 pt-1 border-t border-white/5 text-[9px] font-mono text-white/60 truncate">
            {meta}
          </div>
        )}
      </div>
    </div>
  );
}
